import { useId, useState, type KeyboardEvent } from 'react';
import { useCreateDdRequest, useSetDdRequestRequired, useUpdateDdRequest } from '@/api/hooks';
import { ApiError, Button, Input, readableCategoryName } from '@dataroom/shared';
import type { DdPhaseSummary, DdRequest, UpdateDdRequestRequest } from '@dataroom/shared';

/** An existing category a new item can go into: its path and how it reads. */
export interface CategoryOption {
  phaseId: string;
  path: string[];
  label: string;
}

/** "Bank Docs › Statements" or "Bank Docs > Statements" → its segments. */
function parsePath(text: string): string[] {
  return text
    .split(/\s*[›>]\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const fieldClass =
  'h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-ink max-md:min-h-tap';

/**
 * Add a checklist item IN PLACE (redesign slice 5), replacing the "Add a requested
 * item" dialog that filed every new item outside the category tree. At a
 * category's foot it goes into that category; from the toolbar the phase and the
 * category are chosen — an existing category, or a new one typed as a path
 * ("Tax Docs › State"). It is sent with its `categoryPath`, so it lands in its
 * category; `onAdded` then shows it (the card scrolls to it and highlights it).
 *
 * With `editing` it EDITS that item instead (redesign slice 7, row ⋯ → Edit): the
 * same fields pre-filled, phase and category always offered — so a typo is fixed,
 * a mis-filed item re-filed and a legacy item moved into a phase, keeping its
 * links. Only what changed is sent.
 */
export function AddItemRow({
  roomId,
  phases,
  categories,
  fixed,
  editing,
  onAdded,
  onCancel,
}: {
  roomId: string;
  /** The room's real phases (for the toolbar's phase picker). */
  phases: DdPhaseSummary[];
  /** Existing categories per phase (for the toolbar's category picker). */
  categories: CategoryOption[];
  /** Pre-filled phase and category (the "+ Add item" at a category's foot). */
  fixed?: { phaseId: string; path: string[] };
  /** Edit this item instead of adding one. */
  editing?: DdRequest;
  onAdded: (created: DdRequest, where: string) => void;
  onCancel: () => void;
}) {
  const createItem = useCreateDdRequest(roomId);
  const setRequired = useSetDdRequestRequired(roomId);
  const updateItem = useUpdateDdRequest(roomId);
  // A plain id (no colons): it is used in the input's `list` reference.
  const listId = `cat-options${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  // Editing: the item's own values (its OWN due date — not the phase's it inherits).
  const startCategory = editing
    ? (categories.find(
        (c) =>
          c.phaseId === editing.phaseId &&
          c.path.length === editing.categoryPath.length &&
          c.path.every((seg, i) => seg === editing.categoryPath[i]),
      )?.label ?? editing.categoryPath.map(readableCategoryName).join(' › '))
    : '';
  const [phaseId, setPhaseId] = useState(
    editing ? (editing.phaseId ?? '') : (fixed?.phaseId ?? phases[0]?.phaseId ?? ''),
  );
  const [category, setCategory] = useState(startCategory);
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [title, setTitle] = useState(editing?.title ?? '');
  const [detail, setDetail] = useState(editing?.detail ?? '');
  const [dueDate, setDueDate] = useState(
    editing && !editing.dueDateInherited ? (editing.dueDate ?? '') : '',
  );
  const [optional, setOptional] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = createItem.isPending || setRequired.isPending || updateItem.isPending;
  const options = categories.filter((c) => c.phaseId === phaseId);
  const canSubmit = title.trim().length > 0 && phaseId !== '' && !pending;

  const save = async (item: DdRequest) => {
    const body: UpdateDdRequestRequest = {};
    if (title.trim() !== item.title) body.title = title.trim();
    if ((detail.trim() || null) !== (item.detail ?? null)) body.detail = detail.trim() || null;
    const ownDue = item.dueDateInherited ? null : item.dueDate;
    if ((dueDate || null) !== ownDue) body.dueDate = dueDate || null;
    if (phaseId !== (item.phaseId ?? '')) body.phaseId = phaseId;
    // The category is sent only when it was changed (categories belong to the
    // room, so a move to another phase keeps it).
    if (categoryTouched) {
      const chosen = options.find((o) => o.label === category.trim());
      body.categoryPath = chosen?.path ?? parsePath(category);
    }
    if (Object.keys(body).length === 0) {
      onCancel();
      return;
    }
    const updated = await updateItem.mutateAsync({ requestId: item.id, body });
    const phaseName = phases.find((p) => p.phaseId === updated.phaseId)?.name ?? 'the checklist';
    onAdded(updated, [phaseName, ...updated.categoryPath.map(readableCategoryName)].join(' › '));
  };

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    if (editing) {
      try {
        await save(editing);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not save the item. Try again.');
      }
      return;
    }
    const chosen = options.find((o) => o.label === category.trim());
    const categoryPath = fixed?.path ?? chosen?.path ?? parsePath(category);
    try {
      const created = await createItem.mutateAsync({
        stage: 'due_diligence',
        title: title.trim(),
        phaseId,
        ...(detail.trim() ? { detail: detail.trim() } : {}),
        ...(dueDate ? { dueDate } : {}),
        ...(categoryPath.length > 0 ? { categoryPath } : {}),
      });
      // The create call files every item as required; optional is one more write.
      if (optional) {
        await setRequired.mutateAsync({ requestId: created.id, body: { required: false } });
      }
      const phaseName = phases.find((p) => p.phaseId === phaseId)?.name ?? 'the checklist';
      const where = [phaseName, ...(chosen ? [chosen.label] : categoryPath)].join(' › ');
      onAdded(created, where);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the item. Try again.');
    }
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void submit();
    }
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div
      role="group"
      aria-label={editing ? `Edit ${editing.title}` : 'Add an item'}
      className="space-y-2 rounded-md border border-primary/40 bg-surface-2 p-3"
    >
      {!fixed ? (
        <div className="grid gap-2 sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
            Phase
            <select
              value={phaseId}
              disabled={pending}
              onChange={(e) => {
                setPhaseId(e.target.value);
                if (!editing) setCategory('');
              }}
              className={fieldClass}
            >
              {phaseId === '' ? (
                // A legacy item (from before the room used phases): choose one to move it.
                <option value="" disabled>
                  Choose a phase
                </option>
              ) : null}
              {phases.map((p) => (
                <option key={p.phaseId} value={p.phaseId ?? ''}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
            Category (optional)
            <input
              list={listId}
              value={category}
              disabled={pending}
              placeholder="Pick one, or type a new one"
              onChange={(e) => {
                setCategory(e.target.value);
                setCategoryTouched(true);
              }}
              onKeyDown={onKey}
              className={fieldClass}
            />
            <datalist id={listId}>
              {options.map((o) => (
                <option key={o.label} value={o.label} />
              ))}
            </datalist>
          </label>
        </div>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_10rem]">
        <Input
          aria-label="Requested item"
          placeholder="Requested item, e.g. 2025 Balance sheet"
          value={title}
          autoFocus
          disabled={pending}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={onKey}
          className="h-9"
        />
        <Input
          aria-label="Detail (optional)"
          placeholder="Detail (optional)"
          value={detail}
          disabled={pending}
          onChange={(e) => setDetail(e.target.value)}
          onKeyDown={onKey}
          className="h-9"
        />
        <input
          type="date"
          aria-label="Deadline (optional)"
          value={dueDate}
          disabled={pending}
          onChange={(e) => setDueDate(e.target.value)}
          onKeyDown={onKey}
          className={fieldClass}
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {!editing ? (
          <label className="inline-flex items-center gap-1.5 text-xs text-ink-2">
            <input
              type="checkbox"
              checked={optional}
              disabled={pending}
              onChange={(e) => setOptional(e.target.checked)}
            />
            Optional (still needs a document or a “doesn’t apply”)
          </label>
        ) : null}
        <span className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" disabled={!canSubmit} onClick={() => void submit()}>
            {editing ? (pending ? 'Saving…' : 'Save') : pending ? 'Adding…' : 'Add item'}
          </Button>
        </span>
      </div>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : (
        <p className="text-xs text-ink-3">
          {editing
            ? 'Its documents stay linked. Clear the deadline to use the phase’s due date.'
            : 'Without a deadline it is due on the phase’s due date. The seller sees it once the phase is released.'}
        </p>
      )}
    </div>
  );
}
